/**
 * What Ask Codey looks in (ADR-0044): glossary terms, lessons and cheat sheets, each asked through the gateway, all at
 * once. A section that is closed (404 to non-admins, ADR-0028), fails or finds nothing is simply left out.
 */
export interface Hit {
  title: string;
  text?: string;
  meta?: string;
  url: string;
}

export interface Group {
  kind: 'terms' | 'lessons' | 'sheets';
  label: string;
  hits: Hit[];
}

/** Premium's guided answer on a term search (ADR-0018, ADR-0030), shown first. */
export interface Guided {
  name: string;
  excerpt: string;
  url?: string;
}

export interface Found {
  groups: Group[];
  guided: Guided | null;
}

export type Fetcher = (url: string, init?: RequestInit) => Promise<Response>;

type Json = Record<string, unknown>;
const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v : undefined);
const clean = <T extends object>(o: T): T => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T;

async function ask(fetcher: Fetcher, url: string): Promise<unknown | null> {
  try {
    const response = await fetcher(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    return response.ok ? await response.json() : null;
  } catch {
    return null;
  }
}

export async function search(q: string, fetcher: Fetcher, limit = 5): Promise<Found> {
  const words = encodeURIComponent(q.trim());
  const [terms, lessons, sheets] = await Promise.all([
    ask(fetcher, `/api/glossary/terms/search?q=${words}&pageSize=${limit}`),
    ask(fetcher, `/api/lessons/lessons?q=${words}&pageSize=${limit}`),
    ask(fetcher, `/api/cheat-sheets/sheets?q=${words}`),
  ]);

  const termItems = ((terms as Json | null)?.['items'] as Json[] | undefined) ?? [];
  const termUrl = (t: Json) => `/glossary/${t['slug']}`;
  const groups: Group[] = [
    { kind: 'terms', label: 'Terms', hits: termItems.slice(0, limit).map((t) => clean({
      title: String(t['name'] ?? t['term'] ?? ''), text: str(t['shortDefinition']), meta: str(t['difficultyName']), url: termUrl(t) })) },
    { kind: 'lessons', label: 'Lessons', hits: ((((lessons as Json | null)?.['items']) as Json[] | undefined) ?? []).slice(0, limit).map((l) => clean({
      title: String(l['title'] ?? ''), text: str(l['summary']), meta: str(l['levelName']), url: `/lessons/${l['slug']}` })) },
    { kind: 'sheets', label: 'Cheat sheets', hits: ((Array.isArray(sheets) ? sheets : []) as Json[]).slice(0, limit).map((s) => clean({
      title: String(s['title'] ?? ''), text: str(s['blurb']), meta: str(s['difficultyName']), url: `/cheat-sheets/${s['slug']}` })) },
  ];

  const g = (terms as Json | null)?.['guidedAnswer'] as Json | null | undefined;
  const guidedTerm = g ? termItems.find((t) => t['termId'] === g['termId']) : undefined;
  const guided: Guided | null = g && str(g['excerpt'])
    ? clean({ name: String(g['name'] ?? ''), excerpt: String(g['excerpt']), url: guidedTerm ? termUrl(guidedTerm) : undefined })
    : null;

  return { groups: groups.filter((group) => group.hits.length > 0), guided };
}
